import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Card } from '@/components/ui';
import { Tooltip } from '@/components/ui/Tooltip';
import { BeforeAfterSlider } from '@/components/landing/BeforeAfterSlider';
import { Footer } from '@/components/layout/Footer';
import logo from '@/assets/logo.png';

import beforeImage from '@/assets/landing/before.png';
import afterImage from '@/assets/landing/after.png';

export function LandingPage(): React.JSX.Element {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const scrollToPricing = () => {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation */}
            <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-24">
                        <div className="flex items-center">
                            <img src={logo} alt="List8.app Logo" className="h-20 w-auto" />
                        </div>
                        <div className="flex items-center space-x-4">
                            {currentUser ? (
                                <Button onClick={() => navigate('/dashboard')}>
                                    Go to Dashboard
                                </Button>
                            ) : (
                                <>
                                    <Button variant="ghost" onClick={() => navigate('/login')}>
                                        Log in
                                    </Button>
                                    <Button onClick={() => navigate('/signup')}>
                                        Start Today
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
                        Make Your Listings <br />
                        <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
                            Look Amazing
                        </span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
                        We help real estate agents fix photo problems instantly. Add furniture, remove clutter, or change the lighting. No skills needed.
                    </p>
                    <div className="flex justify-center gap-4">
                        <Button size="lg" onClick={() => navigate('/signup')} className="px-8 py-6 text-lg">
                            Start Today
                        </Button>
                        <Button size="lg" variant="outline" onClick={scrollToPricing} className="px-8 py-6 text-lg">
                            View Pricing
                        </Button>
                    </div>
                </div>

                {/* Service Showcase Section */}
                <div className="space-y-24 mt-20">
                    {/* Row 1: Virtual Staging */}
                    <div className="flex flex-col md:flex-row items-center gap-12">
                        <div className="flex-1 text-left space-y-6">
                            <h3 className="text-3xl font-bold text-gray-900">Virtual Staging</h3>
                            <p className="text-lg text-gray-600">
                                Transform empty, cold rooms into warm, inviting homes. Our AI automatically detects the room type and adds stylish, modern furniture that helps buyers visualize themselves living there.
                            </p>
                            <ul className="space-y-3 text-gray-600">
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span> Increase buyer interest
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span> Sell properties faster
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span> Fraction of the cost of physical staging
                                </li>
                            </ul>
                        </div>
                        <div className="flex-1 w-full">
                            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                                <BeforeAfterSlider
                                    beforeImage={beforeImage}
                                    afterImage={afterImage}
                                    beforeLabel="Empty Room"
                                    afterLabel="Virtually Staged"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Row 2: Item Removal */}
                    <div className="flex flex-col md:flex-row-reverse items-center gap-12">
                        <div className="flex-1 text-left space-y-6">
                            <h3 className="text-3xl font-bold text-gray-900">Item Removal</h3>
                            <p className="text-lg text-gray-600">
                                Declutter messy spaces instantly. Whether it's old furniture, scattered toys, or general clutter, we wipe the slate clean so buyers can focus on the room's dimensions and potential.
                            </p>
                            <ul className="space-y-3 text-gray-600">
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span> Remove distractions
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span> Clean up tenant-occupied homes
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span> Present a move-in ready look
                                </li>
                            </ul>
                        </div>
                        <div className="flex-1 w-full">
                            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                                {/* TODO: Replace with specific Item Removal before/after images */}
                                <BeforeAfterSlider
                                    beforeImage={beforeImage}
                                    afterImage={afterImage}
                                    beforeLabel="Cluttered"
                                    afterLabel="Cleaned"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Row 3: Virtual Destaging */}
                    <div className="flex flex-col md:flex-row items-center gap-12">
                        <div className="flex-1 text-left space-y-6">
                            <h3 className="text-3xl font-bold text-gray-900">Virtual Destaging</h3>
                            <p className="text-lg text-gray-600">
                                Clear out outdated furnishings and decor. We digitally remove existing furniture to present a spacious, empty canvas, perfect for buyers who want to imagine their own style.
                            </p>
                            <ul className="space-y-3 text-gray-600">
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span> Modernize dated interiors
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span> Show true room size
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span> Neutralize personalized spaces
                                </li>
                            </ul>
                        </div>
                        <div className="flex-1 w-full">
                            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                                {/* TODO: Replace with specific Destaging before/after images */}
                                <BeforeAfterSlider
                                    beforeImage={beforeImage}
                                    afterImage={afterImage}
                                    beforeLabel="Furnished"
                                    afterLabel="Empty"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            Everything You Need to Sell Faster
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Professional-grade real estate photo editing tools at your fingertips.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <Card className="p-8 hover:shadow-xl transition-shadow">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                                <span className="text-2xl">🛋️</span>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-xl font-bold text-gray-900">Virtual Staging</h3>
                                <Tooltip content="We add digital furniture to photos of empty rooms so buyers can see how it might look lived in.">
                                    <span className="text-gray-400 cursor-help text-sm border border-gray-300 rounded-full w-5 h-5 flex items-center justify-center">?</span>
                                </Tooltip>
                            </div>
                            <p className="text-blue-600 font-semibold mb-3">5 credits / image</p>
                            <p className="text-gray-600">
                                <strong>Who is this for?</strong> Agents with empty listings.<br />
                                <strong>What does it solve?</strong> Helps buyers imagine living there.
                            </p>
                        </Card>

                        <Card className="p-8 hover:shadow-xl transition-shadow">
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                                <span className="text-2xl">🧹</span>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-xl font-bold text-gray-900">Item Removal</h3>
                                <Tooltip content="We erase unwanted things like messy counters, old furniture, or cars from the driveway.">
                                    <span className="text-gray-400 cursor-help text-sm border border-gray-300 rounded-full w-5 h-5 flex items-center justify-center">?</span>
                                </Tooltip>
                            </div>
                            <p className="text-blue-600 font-semibold mb-3">5 credits / image</p>
                            <p className="text-gray-600">
                                <strong>Who is this for?</strong> Homes that are cluttered or messy.<br />
                                <strong>What does it solve?</strong> Makes rooms look bigger and cleaner.
                            </p>
                        </Card>

                        <Card className="p-8 hover:shadow-xl transition-shadow">
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                                <span className="text-2xl">🪄</span>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-xl font-bold text-gray-900">Virtual Destaging</h3>
                                <Tooltip content="We digitally remove furniture and fixtures to show a spacious, empty room.">
                                    <span className="text-gray-400 cursor-help text-sm border border-gray-300 rounded-full w-5 h-5 flex items-center justify-center">?</span>
                                </Tooltip>
                            </div>
                            <p className="text-blue-600 font-semibold mb-3">5 credits / image</p>
                            <p className="text-gray-600">
                                <strong>Who is this for?</strong> Listings with outdated furniture.<br />
                                <strong>What does it solve?</strong> Shows the potential of the space.
                            </p>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            Simple, Transparent Pricing
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Pay as you go with our credit-based system. No monthly subscriptions or hidden fees—just purchase credits and use them for any service whenever you need. Credits never expire.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Limited Special */}
                        <Card className="relative p-6 border-2 border-green-500 transform hover:-translate-y-1 transition-transform">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white px-4 py-1 rounded-full text-xs font-bold">
                                LIMITED TIME SPECIAL
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Limited Time</h3>
                            <div className="mb-4">
                                <span className="text-4xl font-bold text-gray-900">$3.99</span>
                            </div>
                            <div className="mb-6">
                                <span className="text-2xl font-bold text-blue-600">35</span>
                                <span className="text-gray-600 ml-1">credits</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-6">
                                Perfect for trying out our AI services.
                            </p>
                            <Button className="w-full" onClick={() => navigate('/signup')}>
                                Buy Now
                            </Button>
                        </Card>

                        {/* Starter */}
                        <Card className="p-6 hover:shadow-lg transition-shadow">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Starter</h3>
                            <div className="mb-4">
                                <span className="text-4xl font-bold text-gray-900">$9.99</span>
                            </div>
                            <div className="mb-6">
                                <span className="text-2xl font-bold text-blue-600">100</span>
                                <span className="text-gray-600 ml-1">credits</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-6">
                                Great for individual agents.
                            </p>
                            <Button className="w-full" variant="outline" onClick={() => navigate('/signup')}>
                                Buy Now
                            </Button>
                        </Card>

                        {/* Popular */}
                        <Card className="relative p-6 border-2 border-blue-500 transform hover:-translate-y-1 transition-transform shadow-xl">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-xs font-bold">
                                BEST VALUE
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Popular</h3>
                            <div className="mb-4">
                                <span className="text-4xl font-bold text-gray-900">$39.99</span>
                            </div>
                            <div className="mb-6">
                                <span className="text-2xl font-bold text-blue-600">500</span>
                                <span className="text-gray-600 ml-1">credits</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-6">
                                Most popular choice for professionals.
                            </p>
                            <Button className="w-full" onClick={() => navigate('/signup')}>
                                Buy Now
                            </Button>
                        </Card>

                        {/* Pro */}
                        <Card className="p-6 hover:shadow-lg transition-shadow">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Pro</h3>
                            <div className="mb-4">
                                <span className="text-4xl font-bold text-gray-900">$69.99</span>
                            </div>
                            <div className="mb-6">
                                <span className="text-2xl font-bold text-blue-600">1000</span>
                                <span className="text-gray-600 ml-1">credits</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-6">
                                For high-volume users and agencies.
                            </p>
                            <Button className="w-full" variant="outline" onClick={() => navigate('/signup')}>
                                Buy Now
                            </Button>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <Footer />
        </div>
    );
}
